import { useState } from "react";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { Mail, Phone, MapPin, Send } from "lucide-react";

const ContactForm = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    propertyType: '',
    energyBill: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real application, you would send this data to your backend
    toast({
      title: "Quote Request Submitted!",
      description: "We'll contact you within 24 hours to schedule your free consultation.",
    });
    
    // Reset form
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      propertyType: '',
      energyBill: '',
      message: ''
    });
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section id="contact" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-4 border-secondary text-secondary">
            Get Started
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold mb-6 bg-gradient-solar bg-clip-text text-transparent">
            Get Your Free Solar Quote
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Ready to start saving with solar energy? Fill out the form below and we'll provide you with a custom quote and free consultation.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-solar">
              <CardHeader>
                <CardTitle className="text-2xl text-primary">Request Your Free Quote</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleInputChange('phone', e.target.value)}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="address">Property Address *</Label>
                      <Input
                        id="address"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="propertyType">Property Type *</Label>
                      <Select value={formData.propertyType} onValueChange={(value) => handleInputChange('propertyType', value)}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select property type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="residential">Residential</SelectItem>
                          <SelectItem value="commercial">Commercial</SelectItem>
                          <SelectItem value="industrial">Industrial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="energyBill">Monthly Energy Bill</Label>
                      <Select value={formData.energyBill} onValueChange={(value) => handleInputChange('energyBill', value)}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="under-100">Under $100</SelectItem>
                          <SelectItem value="100-200">$100 - $200</SelectItem>
                          <SelectItem value="200-300">$200 - $300</SelectItem>
                          <SelectItem value="300-500">$300 - $500</SelectItem>
                          <SelectItem value="over-500">Over $500</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">Additional Information</Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us about your energy goals, roof details, or any specific requirements..."
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="mt-2 min-h-[120px]"
                    />
                  </div>

                  <Button type="submit" variant="solar" size="lg" className="w-full">
                    <Send className="mr-2 h-5 w-5" />
                    Get My Free Quote
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-8">
            <Card className="border-0 shadow-card-solar">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-6 text-primary">Contact Information</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 text-solar-blue mr-3" />
                    <div>
                      <p className="font-medium">Phone</p>
                      <p className="text-muted-foreground">(555) 123-SOLAR</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-solar-blue mr-3" />
                    <div>
                      <p className="font-medium">Email</p>
                      <p className="text-muted-foreground">info@pasatcompany.com</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 text-solar-blue mr-3" />
                    <div>
                      <p className="font-medium">Address</p>
                      <p className="text-muted-foreground">123 Solar Street<br />Energy City, EC 12345</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-card-solar bg-gradient-to-br from-solar-light-blue to-background">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4 text-primary">Why Choose PASAT?</h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-solar-green rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Free consultation and custom design</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-solar-green rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>25+ year warranty on all installations</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-solar-green rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Certified and experienced technicians</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-solar-green rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>Financing options available</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-solar-green rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span>24/7 monitoring and support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;